import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { ProfileRoute } from './profile-route';
import { AcceptanceLegalTermsResult } from './shared/modules/acceptance-legal-terms/models/acceptance-legal-terms-result';
import { AcceptanceLegalTermsService } from './shared/modules/acceptance-legal-terms/services/acceptance-legal-terms.service';
import { UserStorageManagerService } from './utils';
import { AuthService } from './utils/cargo-driver-check-in-service/services';

@Injectable({
    providedIn: 'root'
})
export class RoleGuard {

    constructor(
        private router: Router,
        private userStorageManagerService: UserStorageManagerService,
        private acceptanceLegalTermsService: AcceptanceLegalTermsService,
        private authUserService: AuthService
    ) { }

    getUserProfile(): Observable<string> {
        return this.userStorageManagerService.getLastUserChange()
            .pipe(
                map(user => user && user.usr_profile || ''),
                distinctUntilChanged()
            );
    }

    canActivate(
        route: ActivatedRouteSnapshot
    ): Observable<boolean> {
        return this.getUserProfile()
            .pipe(
                take(1),
                switchMap(profile => {
                    if (this.acceptanceLegalTermsService.isRecenteLogin()) {
                        this.acceptanceLegalTermsService.clearFlagRecenteLogin()
                        return of(profile);
                    }

                    return this.acceptanceLegalTermsService.launchIfApplicable()
                        .pipe(
                            take(1),
                            mergeMap(result => {
                                if (result === AcceptanceLegalTermsResult.DECLINED) {
                                    this.authUserService.logout();
                                    this.router.navigateByUrl('/login');
                                    return of(null);
                                }

                                return of(profile);
                            })
                        );
                }),
                map(profile => {
                    if (!(route.data && route.data.expectedRoles && route.data.expectedRoles.length > 0)) {
                        if (route.data.type === 'redirect') {
                            const url = ProfileRoute[profile];

                            if (url) {
                                this.router.navigate(url);
                            }
                        }

                        return true;
                    }

                    if (route.data.expectedRoles.includes(profile)) {
                        return true;
                    }

                    const url = ProfileRoute[profile] || ['session-expired'];

                    this.router.navigate(url);
                    return true;
                }),
                catchError(() => {
                    this.router.navigate(['session-expired']);
                    return of(true);
                })
            );
    }
}